import { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import { EditUploadLineGoodDialog } from '../../../dialogs/settings/data-import/edit-upload-line-good-dialog'
import { IEditFileUploadLineGood, IFileUploadLineGood } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'
import { useNavigate } from 'react-router-dom'
import ManufacturerService from '../../../../shared/services/manufacturer/manufacturer.service'
import { IManufacturer } from '../../../../shared/services/manufacturer/manufacturer.model'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { Chip, Tooltip, IconButton, Stack } from '@mui/material'
import { EditOutlined } from '@mui/icons-material'

export const CsvUploadGood: FunctionComponent<{
  fileUploadLineGoods: IFileUploadLineGood[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uploadLine: IFileUploadLineGood) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineGood | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineGood) => void,
  status: string,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  errorCode: number,
  errorMessage: string,
}> = ({
  fileUploadLineGoods,
  editClickHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setOrder,
  setOrderBy,
  setPerPage,
  setSelected,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  status,
  deleteHandler,
  isSubmitting,
  errorCode,
  errorMessage,
}) => {
    const navigate = useNavigate()
    //Services
    const manufacturerService = new ManufacturerService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [allManufacturers, setAllManufacturers] = useState<IManufacturer[]>()

    useEffect(() => {
      manufacturerService.getAll()
        .then(response => {
          setAllManufacturers(response)
        })
        .catch(err => console.log(err))
    }, [])

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []

        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...(status !== 'discarded' ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'name', numeric: false, label: screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' }) + ' Name', sortable: true },
        { id: 'modelNumber', numeric: false, label: 'Model Number', sortable: true },
        { id: 'typeOfGood', numeric: false, label: 'Type Of Good', sortable: true },
        { id: 'category', numeric: false, label: 'Category', sortable: true },
        { id: 'manufacturer', numeric: false, label: 'Manufacturer', sortable: true },
        { id: 'unit', numeric: false, label: 'Unit', sortable: false },
        { id: 'kgCarbonEmission', numeric: false, label: 'Emissions (kg CO2e per Unit)', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineGoods || [])
      : fileUploadLineGoods?.map((uploadLine: IFileUploadLineGood) => {
        return {
          id: uploadLine.id,
          modelNumber: uploadLine.modelNumber,
          name: uploadLine.name,
          typeOfGood: uploadLine.typeOfGood,
          category: uploadLine.category,
          unit: `${uploadLine.unitAmount ? uploadLine.unitAmount : ''} ${uploadLine.unitName ? uploadLine.unitName : ''}`,
          kgCarbonEmission: uploadLine.kgCarbonEmission,
          manufacturer: uploadLine.manufacturerName,
          action: status === 'validated' ? (
            <TableActionButtons
              id={uploadLine.id}
              includeEdit={true}
              includeDelete={true}
              onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
              onDelete={(id: string) => deleteHandler(id)}
            />
          ) : status === 'completed' ? (
            <TableActionButtons
              id={uploadLine.id}
              includeView={true}
              onView={() => uploadLine.capitalGoodId ? viewTransactionClickHandler(uploadLine.capitalGoodId) : undefined}
              disableButton={!uploadLine.capitalGoodId || uploadLine.capitalGoodId.length === 0}
            />
          ) : null
        }
      })

    const viewTransactionClickHandler = (capitalGoodId: string) => {
      navigate(`/organisation/products/${capitalGoodId}`)
    }

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => ({ id: row }));
      setValidatedUploadLines(formatted)
    };

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };


    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          onOrderChange={handleOrderChange}
          selectByIcon={(status === 'validated' || status === 'pending') ? false : true}
          selectByCheckboxOnly={(status === 'validated' || status === 'pending') ? true : false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          haveCheckboxSelect={(status === 'validated' || status === 'pending') ? true : false}
          perPage={perPage}
          setPerPage={setPerPage} />
        {editUploadLine && allManufacturers && openDialog &&
          <EditUploadLineGoodDialog
            open={openDialog}
            manufacturers={allManufacturers}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine}
            errorCode={errorCode || 0}
            errorMessage={errorMessage || ''}
          />}
      </>
    )
  }